import React, { useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import {
  activateBoldSubscriptionSagaAction,
  pauseBoldSubscriptionSagaAction,
} from "../../../redux/saga/my-account/saga-actions";
import {
  Button,
  DatePicker,
  Divider,
  DividerVariants,
  Iconography,
  IconographySizes,
  Loader,
  Modal,
  TextField,
  Toggle,
} from "@hid-galaxy-ui/galaxy-react";
import convertToNoDecimalValue from "../../../utils/convertToNoDecimalValue";
import ServiceContractImage from "../../../common/assets/images/contract-image.png";
import { ContractType } from "../../../redux/slices/my-account/Types";
import { updateContractSagaAction } from "../../../redux/saga/my-account/saga-actions";
import { getValidDateOrNull } from "../../../utils/getValidDateOrNull";
import { usePartnerTabPermission } from "../../../contexts/PartnerPermissionContext";
import "./ServiceContractDetailModal.scss";

interface ServiceContractDetailModalProps {
  serviceContractDetail: ContractType;
  setDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEditModal?: boolean;
}

const ServiceContractDetailModal: React.FC<ServiceContractDetailModalProps> = ({
  serviceContractDetail,
  setDetailModalOpen,
  isEditModal = false,
}) => {
  const dispatch = useAppDispatch();
  const { subscriptionID, status } = useAppSelector(
    (state) => state.my_account
  );
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { readOnly: tabReadOnly } = usePartnerTabPermission();

  const [contractDetails, setContractDetails] = React.useState(() => ({
    endDate: getValidDateOrNull(serviceContractDetail.end_date),
    startDate: getValidDateOrNull(serviceContractDetail.start_date),
    salesforce_opportunity: serviceContractDetail.salesforce_opportunity || "",
    oracle_channel: serviceContractDetail.oracle_channel?.toString() || "",
  }));
  const [subscriptionLoading, setSubscriptionLoading] =
    React.useState<boolean>(false);
  const [subscriptionToggled, setSubscriptionToggled] = React.useState<boolean>(
    serviceContractDetail.subscription_status === "active"
  );

  const isPartnerInactive = partnerDetails?.status === "Inactive";
  const contractReadOnly = tabReadOnly || isPartnerInactive;
  const canEditContract = !contractReadOnly;
  const isSupportContract =
    serviceContractDetail.collection.toLowerCase() === "support_service";
  const showContractDetailFields =
    isEditModal &&
    serviceContractDetail.source === "service" &&
    isSupportContract &&
    !isPartnerInactive;

  const formatDateForDB = useCallback((date: Date | null) => {
    return date ? date.toISOString() : null;
  }, []);

  const isFormChanged = useMemo(() => {
    const originalStartDate = formatDateForDB(
      getValidDateOrNull(serviceContractDetail.start_date)
    );
    const originalEndDate = formatDateForDB(
      getValidDateOrNull(serviceContractDetail.end_date)
    );
    const currentStartDate = formatDateForDB(contractDetails.startDate);
    const currentEndDate = formatDateForDB(contractDetails.endDate);

    return !(
      originalStartDate === currentStartDate &&
      originalEndDate === currentEndDate &&
      (serviceContractDetail.salesforce_opportunity || "") ===
        contractDetails.salesforce_opportunity &&
      (serviceContractDetail.oracle_channel?.toString() || "") ===
        contractDetails.oracle_channel
    );
  }, [
    serviceContractDetail.start_date,
    serviceContractDetail.end_date,
    serviceContractDetail.salesforce_opportunity,
    serviceContractDetail.oracle_channel,
    contractDetails,
    formatDateForDB,
  ]);

  React.useEffect(() => {
    if (subscriptionID === serviceContractDetail.subscription_id) {
      setSubscriptionLoading(status.subscriptionLoading);
    }
  }, [
    status.subscriptionLoading,
    subscriptionID,
    serviceContractDetail.subscription_id,
  ]);

  React.useEffect(() => {
    if (
      !subscriptionLoading &&
      subscriptionID === serviceContractDetail.subscription_id
    ) {
      if (
        serviceContractDetail.subscription_status === "active" &&
        !subscriptionToggled
      ) {
        setSubscriptionToggled(true);
      } else if (
        serviceContractDetail.subscription_status !== "active" &&
        subscriptionToggled
      ) {
        setSubscriptionToggled(false);
      }
    }
  }, [
    subscriptionLoading,
    serviceContractDetail,
    subscriptionID,
    subscriptionToggled,
  ]);

  const handleSubscriptionToggle = useCallback(
    (e: any) => {
      if (contractReadOnly) return;
      const isChecked = e.target.checked;
      setSubscriptionToggled(isChecked);
      dispatch(
        isChecked
          ? activateBoldSubscriptionSagaAction(
              serviceContractDetail.subscription_id
            )
          : pauseBoldSubscriptionSagaAction(
              serviceContractDetail.subscription_id
            )
      );
    },
    [
      contractReadOnly,
      dispatch,
      serviceContractDetail.subscription_id,
    ]
  );

  const onModalClose = useCallback(() => {
    setDetailModalOpen(false);
  }, [setDetailModalOpen]);

  const handleDateChange = useCallback(
    (date: [Date | null, Date | null] | Date | null, name: string) => {
      if (contractReadOnly) return;
      setContractDetails((prev) => ({
        ...prev,
        [name]: date,
      }));
    },
    [contractReadOnly]
  );

  const handleTextFieldChange = useCallback(
    (value: string, name: string) => {
      if (contractReadOnly) return;
      setContractDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [contractReadOnly]
  );

  const onSaveClick = useCallback(() => {
    if (contractReadOnly) return;
    dispatch(
      updateContractSagaAction({
        ID: serviceContractDetail.ID,
        partnerID: serviceContractDetail.partner_id,
        startDate: formatDateForDB(contractDetails.startDate),
        endDate: formatDateForDB(contractDetails.endDate),
        salesforce_opportunity: contractDetails.salesforce_opportunity,
        oracle_channel: contractDetails.oracle_channel
          ? parseInt(contractDetails.oracle_channel)
          : null,
        setModalOpen: setDetailModalOpen,
      })
    );
  }, [
    contractReadOnly,
    dispatch,
    serviceContractDetail.ID,
    serviceContractDetail.partner_id,
    contractDetails,
    formatDateForDB,
    setDetailModalOpen,
  ]);

  const renderSubscriptionToggle = () => {
    if (!canEditContract || !serviceContractDetail.subscription_id) {
      return null;
    }

    return (
      <>
        <Divider mode={DividerVariants.Content} />
        <div className="subscription-toggle">
          <div className="left">
            <div className="message">
              Enable or Disable this subscription if you wish to pause.
            </div>
          </div>
          <div className="right">
            <Toggle
              id="subscription-toggle"
              labelON="Enabled"
              labelOFF="Disabled"
              onClick={handleSubscriptionToggle}
              toggled={subscriptionToggled}
              disabled={
                subscriptionLoading ||
                serviceContractDetail.status !== "Active" ||
                serviceContractDetail.subscription_status === "inactive"
              }
            />
          </div>
        </div>
      </>
    );
  };

  const renderFormFields = () => {
    if (!showContractDetailFields) {
      return null;
    }

    return (
      <>
        <Divider mode={DividerVariants.Content} />
        <div className="input-wrapper">
          <div className="row">
            <TextField
              onChange={(event) => {
                handleTextFieldChange(
                  event.currentTarget.value,
                  "salesforce_opportunity"
                );
              }}
              id="salesforce_opportunity"
              name="salesforce_opportunity"
              label="Salesforce Opportunity"
              placeholder="Salesforce Opportunity"
              value={contractDetails.salesforce_opportunity}
              theme="dark"
              disabled={contractReadOnly}
            />
            <TextField
              onChange={(event) => {
                const numericValue = event.currentTarget.value.replace(
                  /[^0-9]/g,
                  ""
                );
                handleTextFieldChange(numericValue, "oracle_channel");
              }}
              id="oracle_channel"
              name="oracle_channel"
              label="Oracle Channel"
              placeholder="Oracle Channel"
              value={contractDetails.oracle_channel}
              theme="dark"
              disabled={contractReadOnly}
            />
          </div>
          <div className="row">
            <DatePicker
              onChange={(date) => handleDateChange(date, "startDate")}
              id="startDate"
              name="startDate"
              label="Start Date"
              format="dd-MMM-yyyy"
              placeholder="dd-mmm-yyyy"
              value={contractDetails.startDate}
              isRequired={true}
              disabled={contractReadOnly}
            />
            <DatePicker
              onChange={(date) => handleDateChange(date, "endDate")}
              id="endDate"
              name="endDate"
              label="End Date"
              format="dd-MMM-yyyy"
              placeholder="dd-mmm-yyyy"
              value={contractDetails.endDate}
              isRequired={true}
              disabled={contractReadOnly}
            />
          </div>
        </div>
      </>
    );
  };

  const renderActionButtons = () => {
    if (showContractDetailFields) {
      const saveDisabled =
        contractReadOnly ||
        !isFormChanged ||
        status.updateContractLoading;

      return (
        <div
          className="button-group"
          style={{ justifyContent: "space-between" }}
        >
          <Button variant="secondary" label="Cancel" onClick={onModalClose} />
          <div className="save-button-wrapper">
            <Button
              variant="primary"
              label="Save"
              disabled={saveDisabled}
              onClick={onSaveClick}
            />
            {status.updateContractLoading && <Loader />}
          </div>
        </div>
      );
    }

    return (
      <div className="button-group" style={{ justifyContent: "center" }}>
        <Button variant="primary" label="Close" onClick={onModalClose} />
      </div>
    );
  };

  return (
    <Modal
      width={540}
      isDisplayCloseIcon={false}
      className="contract-detail-modal"
    >
      <div className="close-btn">
        <span onClick={onModalClose}>
          <Iconography icon="circleX" size={IconographySizes.Medium} />
        </span>
      </div>
      <h1 className="title">Service Contract Details</h1>
      <div className="detail-body">
        <div className="image-wrapper">
          <img src={ServiceContractImage} alt="contract-img" />
        </div>
        <div className="info-1">
          <div className="name">{serviceContractDetail.contract_name}</div>
          <div className="collection">
            {serviceContractDetail.collection.replaceAll("_", " ")}
          </div>
        </div>
        <div className="info-2">
          <div className="tile">
            <Iconography icon="moneyBill_1" size={IconographySizes.Medium} />
            <label>Price</label>
            <p>
              $ {convertToNoDecimalValue(serviceContractDetail.contract_amount)}
            </p>
          </div>
          <div className="tile">
            <Iconography icon="circleInfo" size={IconographySizes.Medium} />
            <label>Part Number</label>
            <p>{serviceContractDetail.sku || "-"}</p>
          </div>
          <div className="tile">
            <Iconography icon="calendar" size={IconographySizes.Medium} />
            <label>Type</label>
            <p>{serviceContractDetail.product_type ?? "One Time"}</p>
          </div>
        </div>
        {renderSubscriptionToggle()}
        {renderFormFields()}
        {renderActionButtons()}
      </div>
    </Modal>
  );
};

export default ServiceContractDetailModal;
